import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import { useSeriesShowContext } from "../SeriesShowApp";
import { Button } from "@opendate-app/setlist";

const POPUP_STYLE = {
  border: "none",
  borderRadius: 8,
  padding: 0,
  width: "min(520px, calc(100vw - 40px))",
  textAlign: "left",
};

const KIND_LABELS = {
  season_pass: "Season Pass",
  flex_pack: "Flex Pack",
};

// The stamp action returns calendar_event ids; map them back to a readable show
// label from the schedule already in the dashboard payload.
const useShowLabeler = (schedule) => (calendarEventId) => {
  const row = (schedule || []).find((r) => r.calendar_event_id === calendarEventId);
  if (!row) return `Show #${calendarEventId}`;
  const date = new Date(row.start_time).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  return row.name ? `${date} — ${row.name}` : date;
};

const ShowList = ({ ids, labelFor, testId }) => (
  <ul className="mb-0 pl-3" data-testid={testId}>
    {ids.map((id) => <li key={id}>{labelFor(id)}</li>)}
  </ul>
);

const RedemptionSetupModal = ({ product, onClose }) => {
  const { series, handleStampRedemption } = useSeriesShowContext();
  const labelFor = useShowLabeler(series.schedule);

  const [settlementAmount, setSettlementAmount] = useState("");
  const [allotment, setAllotment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!product) return;
    setSettlementAmount(product.redemption_settlement_amount == null ? "" : String(product.redemption_settlement_amount));
    setAllotment(product.redemption_allotment == null ? "" : String(product.redemption_allotment));
    setError(null);
    setResult(null);
    setSubmitting(false);
  }, [product]);

  const open = Boolean(product);
  const kindLabel = product ? (KIND_LABELS[product.product_kind] || product.product_kind) : "";
  const isValid = Number(settlementAmount) > 0
    && allotment !== ""
    && Number.isInteger(Number(allotment))
    && Number(allotment) >= 0;

  const submit = async () => {
    if (submitting || !isValid) return;
    try {
      setSubmitting(true);
      setError(null);
      setResult(null);
      const data = await handleStampRedemption(product.id, {
        settlement_amount: settlementAmount,
        allotment,
      });
      setResult(data);
    } catch (e) {
      const respData = e && e.response && e.response.data;
      const apiErrors = respData && respData.errors;
      setError(
        Array.isArray(apiErrors)
          ? apiErrors.join(" ")
          : apiErrors || "Could not set up redemption. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup open={open} modal className="popup-modal" contentStyle={POPUP_STYLE} onClose={onClose} closeOnDocumentClick>
      <div className="popup-modal-container">
        <div className="row m-0 series-confirm-header">
          <div className="col series-confirm-header-col">
            <p className="mb-0 series-confirm-title"><strong>Set up redemption</strong></p>
          </div>
          <div className="col-xs-auto d-flex align-items-center">
            <a href="#" className="text-muted series-confirm-close" onClick={(e) => { e.preventDefault(); onClose(); }}>
              <i className="fa fa-times" />
            </a>
          </div>
        </div>
        <div className="row m-0">
          <div className="col-12 series-confirm-body">
            <p className="text-muted mb-3">
              Auto-create the hidden $0 redemption ticket type for <strong>{product && product.name}</strong>{" "}
              ({kindLabel}) on every eligible show. The type is named after this product; each redemption credits the
              artist settlement by the amount below.
            </p>

            <div className="mb-3">
              <label className="form-label"><strong>Settlement amount (per redemption)</strong></label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  data-testid="redemption-settlement-amount"
                  placeholder="e.g. 40.00"
                  value={settlementAmount}
                  onChange={(e) => setSettlementAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label"><strong>Per-show allotment</strong></label>
              <input
                type="number"
                min="0"
                step="1"
                className="form-control"
                data-testid="redemption-allotment"
                placeholder="Redemptions available per show"
                value={allotment}
                onChange={(e) => setAllotment(e.target.value)}
              />
            </div>

            {error && (
              <div className="alert alert-danger py-2" data-testid="redemption-error">
                <div>{error}</div>
              </div>
            )}

            {result && (
              <div className="alert alert-success py-2" data-testid="redemption-result">
                <div className="mb-1">
                  <strong>Stamped {result.stamped.length} show{result.stamped.length === 1 ? "" : "s"}.</strong>
                </div>
                {result.stamped.length > 0 && (
                  <ShowList ids={result.stamped} labelFor={labelFor} testId="redemption-stamped" />
                )}
                {result.skipped_ineligible.length > 0 && (
                  <div className="mt-2">
                    <span>
                      Skipped {result.skipped_ineligible.length} ineligible show
                      {result.skipped_ineligible.length === 1 ? "" : "s"} (seated or locking off):
                    </span>
                    <ShowList ids={result.skipped_ineligible} labelFor={labelFor} testId="redemption-skipped" />
                  </div>
                )}
              </div>
            )}

            <div className="d-flex justify-content-end series-confirm-actions">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>
                {result ? "Done" : "Cancel"}
              </Button>
              {!result && (
                <Button
                  variant="primary"
                  data-testid="redemption-submit"
                  onClick={submit}
                  disabled={submitting || !isValid}
                >
                  {submitting ? "Setting up…" : "Auto-create on eligible shows"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

RedemptionSetupModal.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    product_kind: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

RedemptionSetupModal.defaultProps = {
  product: null,
};

export default RedemptionSetupModal;
